import React, { useContext, useEffect } from 'react';
import { RicosContext, RicosViewerContext } from 'ricos-context';
import { mergeStyles } from 'wix-rich-content-common';
import classnames from 'classnames';
import { f as fileStyles, g as getIcon, E as ErrorIcon, L as LoaderIcon, R as ReadyIcon, D as DownloadIcon } from './file-upload-viewer2.js';
import { debounce } from 'lodash';
import { FILE_UPLOAD_TYPE } from './viewer-loadable.js';
import Tooltip from 'wix-rich-content-common/libs/Tooltip';
import '@loadable/component';
import 'ricos-types';

var getNameWithoutType = function getNameWithoutType(fileName) {
  if (!fileName || !fileName.includes(".")) {
    return fileName;
  }
  var s = fileName.split(".");
  return s.slice(0, s.length - 1).join(".");
};
var resizeWidths = {
  first: 320,
  second: 140,
  third: 100
};
var FileViewer = function FileViewer(_ref) {
  var settings = _ref.settings,
    componentData = _ref.componentData,
    _ref$isLoading = _ref.isLoading,
    isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
    tempDataPlaceHolder = _ref.tempDataPlaceHolder,
    helpers = _ref.helpers;
  var _a;
  var _useContext = useContext(RicosContext),
    theme = _useContext.theme,
    isMobile = _useContext.isMobile,
    t = _useContext.t;
  var _React$useState = React.useState(null),
    downloaderRef = _React$useState[0],
    setDownloaderRef = _React$useState[1];
  var _React$useState2 = React.useState(null),
    fileUploadViewerRef = _React$useState2[0],
    setFileUploadViewerRef = _React$useState2[1];
  var _React$useState3 = React.useState(null),
    resolvedFileUrl = _React$useState3[0],
    setResolvedFileUrl = _React$useState3[1];
  var _React$useState4 = React.useState(false),
    resolvingUrl = _React$useState4[0],
    setResolvingUrl = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    showReadyIcon = _React$useState5[0],
    setShowReadyIcon = _React$useState5[1];
  var _React$useState6 = React.useState(0);
    _React$useState6[0];
    var setCurrentWidth = _React$useState6[1];
  var styles = mergeStyles({
    styles: fileStyles,
    theme: theme
  });
  useEffect(function () {
    var resizer = (window == null ? void 0 : window.ResizeObserver) && new ResizeObserver(debounce(function (entries) {
      setCurrentWidth(Math.round(entries[0].contentRect.width));
    }, 60));
    fileUploadViewerRef && (resizer == null ? void 0 : resizer.observe(fileUploadViewerRef));
    return function () {
      fileUploadViewerRef && (resizer == null ? void 0 : resizer.unobserve(fileUploadViewerRef));
    };
  }, [fileUploadViewerRef]);
  useEffect(function () {
    if (isLoading) {
      switchReadyIcon();
    }
  }, [isLoading]);
  var switchReadyIcon = function switchReadyIcon() {
    setShowReadyIcon(true);
    setTimeout(function () {
      return setShowReadyIcon(false);
    }, 2e3);
  };
  var isInResizeRange = function isInResizeRange(resizeWidth) {
    return (fileUploadViewerRef == null ? void 0 : fileUploadViewerRef.clientWidth) && fileUploadViewerRef.clientWidth < resizeWidth;
  };
  var renderContainerWithoutLink = function renderContainerWithoutLink() {
    var _classnames;
    var _ref2 = tempDataPlaceHolder ? tempDataPlaceHolder : componentData,
      name = _ref2.name,
      type = _ref2.type;
    return /* @__PURE__ */React.createElement("div", {
      className: classnames(styles.file_upload_link, (_classnames = {}, _classnames[styles.width_three] = isInResizeRange(resizeWidths.third), _classnames))
    }, renderViewerBody({
      name: name,
      type: type
    }));
  };
  var renderIcon = function renderIcon(Icon) {
    var showLoader = isLoading || resolvingUrl;
    var showFileIcon = !showLoader && !showReadyIcon && isMobile || !isMobile && Icon;
    if (showFileIcon) {
      var _classnames2;
      return /* @__PURE__ */React.createElement(Icon, {
        styles: styles,
        className: classnames(styles.file_upload_type_icon, (_classnames2 = {}, _classnames2[styles.width_three] = isInResizeRange(resizeWidths.third), _classnames2))
      });
    } else {
      return /* @__PURE__ */React.createElement("div", {
        className: isMobile ? styles.mobile_status_icon : styles.file_upload_state
      }, componentData.error ? /* @__PURE__ */React.createElement(ErrorIcon, null) : showLoader ? /* @__PURE__ */React.createElement(LoaderIcon, {
        className: styles.file_loader_icon
      }) : showReadyIcon ? /* @__PURE__ */React.createElement(ReadyIcon, null) : !isInResizeRange(resizeWidths.first) && /* @__PURE__ */React.createElement(DownloadIcon, null));
    }
  };
  var sizeToString = function sizeToString(size) {
    return size < 1e3 ? size + "B" : size < 1e6 ? Math.round(size / 1e3) + "KB" : (size / 1e6).toFixed(2) + "MB";
  };
  var getFileInfoString = function getFileInfoString(type) {
    if (componentData.error) {
      return {
        infoString: t("UploadFile_Error_Generic_Item"),
        infoStyle: styles.file_upload_text_error
      };
    }
    var translationKey = isLoading || resolvingUrl ? "UploadFile_Viewer_Loader" : "UploadFile_Viewer_Download";
    var infoString = t(translationKey, {
      fileType: type == null ? void 0 : type.toUpperCase()
    });
    var size = componentData.size || (tempDataPlaceHolder == null ? void 0 : tempDataPlaceHolder.size);
    if (size) {
      infoString = infoString + " \u2022 " + sizeToString(size);
    }
    return {
      infoString: infoString,
      infoStyle: styles.file_upload_type
    };
  };
  var renderViewerBody = function renderViewerBody(_ref3) {
    var _classnames3, _classnames4;
    var name = _ref3.name,
      type = _ref3.type;
    var nameWithoutType = getNameWithoutType(name);
    var Icon = getIcon(type);
    var _getFileInfoString = getFileInfoString(type),
      infoString = _getFileInfoString.infoString,
      infoStyle = _getFileInfoString.infoStyle;
    return /* @__PURE__ */React.createElement(React.Fragment, null, renderIcon(Icon), !isMobile && renderIcon(), !isInResizeRange(resizeWidths.third) && /* @__PURE__ */React.createElement("div", {
      className: classnames(styles.file_upload_text_container, (_classnames3 = {}, _classnames3[styles.width_two] = isInResizeRange(resizeWidths.second), _classnames3))
    }, /* @__PURE__ */React.createElement("div", {
      className: styles.file_upload_name_container
    }, !isInResizeRange(resizeWidths.second) && /* @__PURE__ */React.createElement("div", {
      className: styles.file_upload_name
    }, nameWithoutType), type && /* @__PURE__ */React.createElement("div", {
      className: classnames(styles.file_upload_extension, (_classnames4 = {}, _classnames4[styles.width_one] = isInResizeRange(resizeWidths.first), _classnames4))
    }, "." + type)), /* @__PURE__ */React.createElement("div", {
      className: infoStyle
    }, infoString)));
  };
  var renderViewer = function renderViewer(fileUrl2) {
    var _classnames5;
    var name = componentData.name,
      type = componentData.type,
      error = componentData.error;
    var downloadTarget = settings.downloadTarget;
    if (error || tempDataPlaceHolder) {
      return renderContainerWithoutLink();
    }
    return /* @__PURE__ */React.createElement("a", {
      href: fileUrl2,
      target: downloadTarget,
      className: classnames(styles.file_upload_link, (_classnames5 = {}, _classnames5[styles.width_three] = isInResizeRange(resizeWidths.third), _classnames5)),
      onClick: onFileClick
    }, renderViewerBody({
      name: name,
      type: type
    }));
  };
  var renderFileUrlResolver = function renderFileUrlResolver() {
    var _classnames6;
    var name = componentData.name,
      type = componentData.type,
      error = componentData.error;
    if (error) {
      return renderContainerWithoutLink();
    }
    var fileUrlResolver = function fileUrlResolver() {
      var _a2, _b;
      if (!resolvingUrl) {
        setResolvingUrl(true);
        settings.resolveFileUrl({
          id: (_a2 = componentData == null ? void 0 : componentData.src) == null ? void 0 : _a2.id,
          type: componentData.type,
          path: componentData.path,
          mimeType: componentData.mimeType,
          name: componentData.name,
          size: componentData.size,
          pdfSettings: componentData.pdfSettings,
          privacy: ((_b = componentData == null ? void 0 : componentData.src) == null ? void 0 : _b["private"]) ? "private" : "public"
        }).then(function (resolvedFileUrl2) {
          setResolvedFileUrl(resolvedFileUrl2);
          setResolvingUrl(false);
          switchReadyIcon();
          if (downloaderRef) {
            downloaderRef.href = resolvedFileUrl2;
            downloaderRef.download = name;
            downloaderRef.click();
          }
        });
      }
    };
    var resolveIfEnter = function resolveIfEnter(ev) {
      var enterEvent = 13;
      if (ev.which === enterEvent) {
        fileUrlResolver();
      }
    };
    return /* @__PURE__ */React.createElement("div", {
      onClick: fileUrlResolver,
      onKeyDown: resolveIfEnter,
      role: "button",
      tabIndex: 0,
      className: classnames(styles.file_upload_link, (_classnames6 = {}, _classnames6[styles.width_three] = isInResizeRange(resizeWidths.third), _classnames6))
    }, renderViewerBody({
      name: name,
      type: type
    }));
  };
  var renderAutoDownloadLinkRef = function renderAutoDownloadLinkRef() {
    var withFileUrlResolver = settings.resolveFileUrl,
      downloadTarget = settings.downloadTarget;
    if (!withFileUrlResolver) {
      return null;
    }
    var target = downloadTarget || "_blank";
    return /* @__PURE__ */React.createElement("a", {
      href: "#0",
      ref: setDownloaderRef,
      style: {
        display: "none"
      },
      target: target
    }, /* @__PURE__ */React.createElement(React.Fragment, null));
  };
  var onFileClick = function onFileClick() {
    var _a2;
    return (
      // @ts-ignore
      (_a2 = helpers == null ? void 0 : helpers.onViewerAction) == null ? void 0 : _a2.call(helpers, FILE_UPLOAD_TYPE, "Click")
    );
  };
  var fileUrl = ((_a = componentData.src) == null ? void 0 : _a.url) || resolvedFileUrl;
  var viewer = fileUrl || tempDataPlaceHolder ? renderViewer(fileUrl) : renderFileUrlResolver();
  return /* @__PURE__ */React.createElement(Tooltip, {
    content: isInResizeRange(resizeWidths.first) && componentData.name,
    tooltipOffset: {
      y: 25
    }
  }, /* @__PURE__ */React.createElement("div", {
    "data-hook": "fileUploadViewer",
    ref: setFileUploadViewerRef,
    className: classnames(styles.file_upload_container, componentData.error && styles.file_upload_error_container)
  }, viewer, renderAutoDownloadLinkRef()));
};

var File = function File(_ref) {
  var node = _ref.node,
    nodeIndex = _ref.nodeIndex,
    NodeViewContainer = _ref.NodeViewContainer;
  var _useContext = useContext(RicosViewerContext),
    config = _useContext.config,
    helpers = _useContext.helpers;
  return /* @__PURE__ */React.createElement(NodeViewContainer, {
    key: node.id,
    node: node,
    nodeIndex: nodeIndex
  }, /* @__PURE__ */React.createElement(FileViewer, {
    componentData: node.fileData,
    settings: config[FILE_UPLOAD_TYPE],
    helpers: helpers
  }));
};

export { File as default };

import 'wix-rich-content-plugin-file-upload/dist/loadable/viewer/es/styles.viewer.min.global.css'